import { useState } from "react";
import { Mail, MapPin, Send, Linkedin, Phone, CheckCircle2 } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    // UI-only — no backend call
    setSent(true);
    setForm({ name: "", email: "", company: "", message: "" });
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative pt-36 md:pt-48 pb-16 md:pb-20 text-center">
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute inset-0 radial-glow" />
        <div className="relative max-w-4xl mx-auto px-6">
          <span className="section-label">Contact</span>
          <h1 className="mt-6 font-semibold tracking-tight leading-[1.08] text-[2rem] sm:text-5xl md:text-6xl lg:text-7xl">
            Let's <span className="gradient-text">talk telecom.</span>
          </h1>
          <p className="mt-6 md:mt-7 text-sm sm:text-base md:text-lg text-gray-400 max-w-[34ch] sm:max-w-xl md:max-w-2xl mx-auto">
            Tell us what you're working on. We'll respond within one business day with next steps.
          </p>
        </div>
      </section>

      {/* Form + Info */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <div className="lg:col-span-7 card-base p-8 md:p-10">
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight">Send us a message</h2>
            <p className="mt-2 text-sm text-gray-400">A senior consultant will reach back to you.</p>

            <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-2 block">Name</label>
                <input
                  required
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                  className="input-base"
                />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-2 block">Email</label>
                <input
                  required
                  type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@operator.com"
                  className="input-base"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-2 block">Company</label>
                <input
                  data-testid="contact-company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="Your operator / company"
                  className="input-base"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-500 mb-2 block">Message</label>
                <textarea
                  required
                  data-testid="contact-message"
                  rows="5"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about your project, timelines and any constraints..."
                  className="input-base resize-none"
                />
              </div>
              <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-4">
                <p className="text-xs text-gray-500 max-w-xs">
                  By submitting, you agree to be contacted about your enquiry.
                </p>
                <button type="submit" data-testid="contact-submit" className="btn-primary">
                  {sent ? (<><CheckCircle2 className="w-4 h-4" /> Message sent</>) : (<>Send message <Send className="w-4 h-4" /></>)}
                </button>
              </div>
            </form>
          </div>

          {/* Info cards */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="card-base p-7" data-testid="contact-email-card">
              <div className="flex items-center gap-4">
                <div className="icon-circle"><Mail className="w-5 h-5" /></div>
                <div>
                  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Email</p>
                  <a href="mailto:support@moxizo.com" className="text-lg font-medium hover:text-cyan-400 transition-colors">support@moxizo.com</a>
                </div>
              </div>
              <p className="mt-4 text-sm text-gray-400">Best for detailed enquiries, RFPs and scoping documents.</p>
            </div>

            <div className="card-base p-7" data-testid="contact-address-card">
              <div className="flex items-center gap-4">
                <div className="icon-circle"><MapPin className="w-5 h-5" /></div>
                <div>
                  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Office</p>
                  <p className="text-lg font-medium leading-snug">401, Synergy, Nagpur<br />Maharashtra 440008</p>
                </div>
              </div>
            </div>

            <div className="card-base p-7" data-testid="contact-social-card">
              <div className="flex items-center gap-4">
                <div className="icon-circle"><Phone className="w-5 h-5" /></div>
                <div>
                  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Connect</p>
                  <p className="text-sm text-gray-400">Follow along on LinkedIn for updates from the field.</p>
                </div>
              </div>
              <div className="mt-5 flex items-center gap-3">
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 rounded-full border border-white/10 hover:border-cyan-400/40 hover:bg-cyan-400/5 flex items-center justify-center transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="card-base overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="icon-circle w-10 h-10"><MapPin className="w-4 h-4" /></div>
                <div>
                  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Visit us</p>
                  <p className="text-sm font-medium">Synergy, Nagpur, Maharashtra</p>
                </div>
              </div>
              <a
                href="https://www.google.com/maps/place/Nagpur,+Maharashtra"
                target="_blank"
                rel="noreferrer"
                className="btn-ghost text-xs"
              >
                Open in Maps
              </a>
            </div>
            <div className="relative">
              <iframe
                title="Moxizo Office Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d119052.79796167577!2d78.98841729726562!3d21.14586889999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd4c0a7ec39e24d%3A0x4f4be5c6d6b0f0f0!2sNagpur%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1700000000000"
                width="100%"
                height="420"
                loading="lazy"
                style={{ border: 0, filter: "invert(92%) hue-rotate(180deg) saturate(0.6) brightness(0.85)" }}
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
